import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../components/LoadingSpinner';
import '../css/news-detail.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faClock, faArrowLeft } from "@fortawesome/free-solid-svg-icons";

const NewsDetail = () => {
    const { id } = useParams();
    const [newsItem, setNewsItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { t, i18n } = useTranslation();

    const formatSimpleDate = (dateInput) => {
        if (!dateInput) return '';
        const isoDate = (typeof dateInput === 'object' && dateInput !== null && dateInput.date)
            ? dateInput.date
            : dateInput;
        const date = new Date(isoDate);
        if (isNaN(date.getTime())) {
            console.error("Invalid time value for date:", dateInput);
            return '...';
        }
        const locale = i18n.language || 'en';
        return date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
    };
    
    const calculateReadTime = (text) => {
        if (!text) return '';
        const words = text.split(/\s+/).length;
        const readTime = Math.ceil(words / 200);
        const pluralKey = readTime === 1 ? 'news.readTimeSingular' : 'news.readTimeMany';
        return `${readTime} ${t(pluralKey, { count: readTime })}`;
    };

    useEffect(() => {
        const fetchNews = async () => {
            setLoading(true);
            setError(null);
            try {
                const initialResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/news/${id}`);
                if (!initialResponse.ok) throw new Error('News item not found.');
                const initialData = await initialResponse.json();

                if (!initialData.group_id) throw new Error('News group not found.');

                const groupResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/news/group/${initialData.group_id}`);
                if (!groupResponse.ok) throw new Error('Failed to fetch news group.');
                const groupData = await groupResponse.json();

                const newsByLang = {};
                groupData.forEach(item => {
                    newsByLang[item.lang] = item;
                });

                const currentNews = newsByLang[i18n.language] || newsByLang['en'] || groupData[0];
                setNewsItem(currentNews);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchNews();
        }
    }, [id, i18n.language]);

    if (loading) {
        return <div className="news-detail-container"><LoadingSpinner /></div>;
    }

    if (error || !newsItem) {
        return (
            <div className="news-detail-container">
                <div className="error-message">
                    <h2>{t('news.errorTitle', 'Error')}</h2>
                    <p>{t('news.notFound', 'News Not Found')}</p>
                    <Link to="/" state={{ scrollTo: '#news-section' }} className="back-button">
                        <FontAwesomeIcon icon={faArrowLeft}/>
                        {t('news.backToNews') || 'Back'}
                    </Link>
                </div>
            </div>
        );
    }

    const bannerStyle = newsItem.image
        ? { backgroundImage: `url(${newsItem.image})` }
        : { backgroundColor: 'rgba(0, 0, 0, 0.6)' };

    return (
        <div className="news-detail-container">
            <div className="news-detail-banner">
                <div className="banner-background" style={bannerStyle}></div>
                <div className="banner-content">
                    <div className="banner-category">{t('news.category', 'Update')}</div>
                    <h1 className="banner-title">{newsItem.title}</h1>
                    <div className="banner-meta">
                        <span><FontAwesomeIcon icon={faCalendarAlt} /> {formatSimpleDate(newsItem.createdAt)}</span>
                        <span><FontAwesomeIcon icon={faClock} /> {calculateReadTime(newsItem.description)}</span>
                    </div>
                </div>
            </div>

            <div className="back-button-container">
                <Link to="/" state={{ scrollTo: '#news-section' }} className="back-button">
                    <FontAwesomeIcon icon={faArrowLeft} />
                    {t('news.backToNews') || 'Back to News'}
                </Link>
            </div>

            <main className="news-detail-main">
                <div className="news-detail-body">
                    <div
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(newsItem.description)
                        }}
                    />
                </div>
            </main>
        </div>
    );
};

export default NewsDetail;